/*
 * Roku singles filter grid.
 *
 * The `singles` collection is over Shopify's 5,000-product cap, so native
 * storefront filters (`collection.filters`) come back empty. This module
 * rebuilds filtering/sorting on top of the Storefront `search` query (which
 * has no 5k cap) and renders the theme's own `.product-card` markup so the
 * grid stays visually identical to native collections.
 *
 * Filter state lives in the URL using Shopify's native `filter.*` param
 * convention so links are shareable and back/forward works.
 */
(function () {
  var root = document.getElementById('SinglesFilterApp');
  if (!root) return;

  var cfg = JSON.parse(document.getElementById('SinglesFilterConfig').textContent);
  // Same-origin call: the Storefront API is also served on the shop's own
  // domain, which avoids cross-origin requests to *.myshopify.com entirely
  // (throttled/error responses there can lack CORS headers and surface as
  // opaque "Failed to fetch" CORS errors).
  var API = location.origin + '/api/' + cfg.apiVersion + '/graphql.json';

  var els = {
    facets: root.querySelector('[data-sf-facets]'),
    facetsMobile: root.querySelector('[data-sf-facets-mobile]'),
    grid: root.querySelector('[data-sf-grid]'),
    count: root.querySelector('[data-sf-count]'),
    sort: root.querySelector('[data-sf-sort]'),
    active: root.querySelector('[data-sf-active]'),
    loadMore: root.querySelector('[data-sf-loadmore]'),
    spinner: root.querySelector('[data-sf-spinner]'),
    sentinel: root.querySelector('[data-sf-sentinel]'),
    empty: root.querySelector('[data-sf-empty]'),
  };

  // gen guards against stale async responses: refresh() bumps it, and any
  // in-flight request from an older generation is discarded on arrival.
  // Without this, changing the sort mid-pagination lets an old response write
  // its cursor into state, and the next page request then sends a cursor from
  // the previous sort ("The cursor sort direction is not consistent with the
  // sort key").
  var state = { after: null, hasNext: false, loading: false, total: 0, streams: [true, false], streamIdx: 0, gen: 0 };

  // Exact facet counts precomputed from the Admin API (build-facet-counts.py
  // publishes assets/singles-facet-counts.json). The search API caps every
  // facet count and totalCount at 1,000, so the UNFILTERED view swaps in
  // these real numbers; filtered views keep the contextual (capped) counts.
  var exactCounts = null;
  var exactCountsReady = cfg.countsUrl
    ? fetch(cfg.countsUrl)
        .then(function (r) {
          if (!r.ok) throw new Error('exact counts fetch failed: HTTP ' + r.status + ' ' + cfg.countsUrl);
          return r.json();
        })
        .then(function (json) { exactCounts = json; })
        .catch(function (err) {
          // Loud, not silent: capped counts still render, but the failure is visible.
          console.error('[singles-filter] exact counts unavailable', err);
        })
    : Promise.resolve();

  function hasActiveFilters() {
    var any = false;
    new URLSearchParams(location.search).forEach(function (v, k) {
      if (k.indexOf('filter.') === 0) any = true;
    });
    return any;
  }

  // Exact count for a facet value in the unfiltered view, else null.
  function exactFacetCount(facetId, valueLabel) {
    if (!exactCounts || hasActiveFilters()) return null;
    var m = exactCounts.facets[facetId];
    return m && typeof m[valueLabel] === 'number' ? m[valueLabel] : null;
  }

  /* ---------- URL <-> filter input helpers ---------- */

  // Build the productFilters array (Storefront input) from the current URL.
  // Availability is handled separately (see availabilityMode) so we can force
  // in-stock-first ordering, so it is always excluded here.
  function readFiltersFromUrl() {
    var p = new URLSearchParams(location.search);
    var filters = [];
    var priceMin = null, priceMax = null;
    p.forEach(function (value, key) {
      if (key === 'filter.v.availability') {
        return; // handled by availabilityMode()
      } else if (key === 'filter.v.price.gte') {
        priceMin = parseFloat(value);
      } else if (key === 'filter.v.price.lte') {
        priceMax = parseFloat(value);
      } else if (key.indexOf('filter.p.m.') === 0) {
        var parts = key.split('.'); // filter.p.m.<ns>.<key>
        filters.push({
          productMetafield: { namespace: parts[3], key: parts[4], value: value },
        });
      } else if (key === 'filter.p.vendor') {
        filters.push({ productVendor: value });
      } else if (key === 'filter.p.product_type') {
        filters.push({ productType: value });
      } else if (key === 'filter.p.tag') {
        filters.push({ tag: value });
      }
    });
    if (priceMin !== null || priceMax !== null) {
      // Only include the bounds that are actually set: an explicit `max: null`
      // makes the Storefront API match nothing.
      var priceInput = {};
      if (priceMin !== null && !isNaN(priceMin)) priceInput.min = priceMin;
      if (priceMax !== null && !isNaN(priceMax)) priceInput.max = priceMax;
      if (Object.keys(priceInput).length) filters.push({ price: priceInput });
    }
    return filters;
  }

  function getSort() {
    return new URLSearchParams(location.search).get('sort_by') || 'relevance';
  }

  function sortToKey(sort) {
    if (sort === 'price-ascending') return { sortKey: 'PRICE', reverse: false };
    if (sort === 'price-descending') return { sortKey: 'PRICE', reverse: true };
    return { sortKey: 'RELEVANCE', reverse: false };
  }

  // Resolve how availability should be handled based on the user's own
  // "Stok Durumu" selection. Sold-out products must ALWAYS render last, so
  // when the user has not restricted availability we split into two streams:
  // in-stock first, then sold-out.
  //   facetAvail: value to apply to the facet/count query (null = both)
  //   streams:    ordered list of availability booleans to page through
  function availabilityMode() {
    var vals = new URLSearchParams(location.search).getAll('filter.v.availability');
    var hasIn = vals.indexOf('1') !== -1;
    var hasOut = vals.indexOf('0') !== -1;
    if (hasIn && !hasOut) return { facetAvail: true, streams: [true] };
    if (hasOut && !hasIn) return { facetAvail: false, streams: [false] };
    return { facetAvail: null, streams: [true, false] };
  }

  function facetFilters(mode) {
    var f = readFiltersFromUrl();
    if (mode.facetAvail !== null) f.push({ available: mode.facetAvail });
    return f;
  }

  function listingFilters(available) {
    var f = readFiltersFromUrl();
    f.push({ available: available });
    return f;
  }

  // Does the URL currently select this filter value?
  function isChecked(filterId, valueLabel) {
    var p = new URLSearchParams(location.search);
    var vals = p.getAll(filterId);
    return vals.indexOf(valueLabel) !== -1;
  }

  function toggleFilterParam(filterId, valueLabel, on) {
    var p = new URLSearchParams(location.search);
    var existing = p.getAll(filterId);
    p.delete(filterId);
    existing.forEach(function (v) {
      if (v !== valueLabel) p.append(filterId, v);
    });
    if (on) p.append(filterId, valueLabel);
    pushState(p);
  }

  function setPrice(min, max) {
    var p = new URLSearchParams(location.search);
    p.delete('filter.v.price.gte');
    p.delete('filter.v.price.lte');
    if (min) p.set('filter.v.price.gte', min);
    if (max) p.set('filter.v.price.lte', max);
    pushState(p);
  }

  function clearAll() {
    var p = new URLSearchParams(location.search);
    var sort = p.get('sort_by');
    var np = new URLSearchParams();
    if (sort) np.set('sort_by', sort);
    pushState(np);
  }

  function pushState(params) {
    var qs = params.toString();
    history.pushState({}, '', location.pathname + (qs ? '?' + qs : ''));
    refresh();
  }

  window.addEventListener('popstate', refresh);

  /* ---------- Storefront query ---------- */

  var PRODUCT_FIELDS = [
    '   title handle onlineStoreUrl availableForSale',
    '   featuredImage{url(transform:{maxWidth:600}) altText width height}',
    '   images(first:2){nodes{url(transform:{maxWidth:600}) width height}}',
    '   priceRange{minVariantPrice{amount}}',
    '   compareAtPriceRange{minVariantPrice{amount}}',
    '   variants(first:1){nodes{id availableForSale}}',
  ].join('\n');

  var QUERY = [
    'query($q:String!,$f:[ProductFilter!],$sk:SearchSortKeys,$rev:Boolean,$n:Int!,$after:String){',
    ' search(query:$q,types:PRODUCT,productFilters:$f,sortKey:$sk,reverse:$rev,first:$n,after:$after){',
    '  totalCount pageInfo{hasNextPage endCursor}',
    '  productFilters{id label type values{id label count input}}',
    '  edges{node{__typename ... on Product{',
    PRODUCT_FIELDS,
    '  }}}',
    ' }',
    '}',
  ].join('');

  // The `search` connection silently caps every query at 1,000 results
  // (pagination returns empty pages past that). The `products` connection has
  // no such cap, but it cannot express metafield or facet filters — only the
  // query-string syntax (tag / vendor / product_type / price / availability).
  // So listings run on `products` whenever the active filters can be
  // expressed as a query string, and only fall back to the capped `search`
  // when a metafield filter (Set, Pokémon, Renk, ...) is selected — those
  // subsets are comfortably under 1,000.
  var PRODUCTS_QUERY = [
    'query($q:String!,$sk:ProductSortKeys,$rev:Boolean,$n:Int!,$after:String){',
    ' products(query:$q,sortKey:$sk,reverse:$rev,first:$n,after:$after){',
    '  pageInfo{hasNextPage endCursor}',
    '  edges{node{',
    PRODUCT_FIELDS,
    '  }}',
    ' }',
    '}',
  ].join('');

  function quoteTerm(v) {
    return '"' + String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  // Metafield facets whose values are mirrored as product tags (kept in sync
  // by backfill-facet-tags.py from the card.* metafields), letting those
  // views run on the uncapped products() engine. Values map facet label ->
  // tag; card_type tags equal the facet value itself.
  var TAG_MIRROR = {
    'filter.p.m.card.language': {
      'İngilizce': 'English', 'Japonca': 'Japanese', 'Çince': 'Chinese',
      'Korece': 'Korean', 'İtalyanca': 'Italian', 'Almanca': 'German',
      'Fransızca': 'French', 'İspanyolca': 'Spanish', 'Portekizce': 'Portuguese',
    },
    'filter.p.m.card.card_type': 'identity',
  };

  function mirrorTag(key, value) {
    var map = TAG_MIRROR[key];
    if (!map) return null;
    if (map === 'identity') return value;
    return map[value] || null;
  }

  // Translate current URL filters into products() query-string terms.
  // Values of the same facet are OR'd (native filter semantics); facets are
  // AND'd against each other. Returns null when a filter has no query-string
  // equivalent, which forces the (1,000-capped) search engine for that view.
  function productsQueryString(available) {
    var p = new URLSearchParams(location.search);
    var groups = {}; // key -> [term, ...]
    var priceTerms = [];
    var ok = true;
    p.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0) return;
      if (key === 'filter.v.availability') return; // handled via streams
      if (key === 'filter.v.price.gte') {
        if (value !== '' && !isNaN(parseFloat(value))) priceTerms.push('variants.price:>=' + parseFloat(value));
        return;
      }
      if (key === 'filter.v.price.lte') {
        if (value !== '' && !isNaN(parseFloat(value))) priceTerms.push('variants.price:<=' + parseFloat(value));
        return;
      }
      var term = null;
      if (key === 'filter.p.tag') term = 'tag:' + quoteTerm(value);
      else if (key === 'filter.p.vendor') term = 'vendor:' + quoteTerm(value);
      else if (key === 'filter.p.product_type') term = 'product_type:' + quoteTerm(value);
      else {
        var tag = mirrorTag(key, value);
        if (tag !== null) term = 'tag:' + quoteTerm(tag);
      }
      if (term === null) { ok = false; return; }
      (groups[key] = groups[key] || []).push(term);
    });
    if (!ok) return null;
    var terms = [cfg.scope];
    Object.keys(groups).forEach(function (key) {
      var g = groups[key];
      terms.push(g.length === 1 ? g[0] : '(' + g.join(' OR ') + ')');
    });
    priceTerms.forEach(function (t) { terms.push(t); });
    // products() silently ignores `available_for_sale:false` (returns
    // in-stock items!); only the negated form selects sold-out products.
    terms.push(available ? 'available_for_sale:true' : '-available_for_sale:true');
    return terms.join(' AND ');
  }

  function productsSortKey(sort) {
    if (sort === 'price-ascending') return { sortKey: 'PRICE', reverse: false };
    if (sort === 'price-descending') return { sortKey: 'PRICE', reverse: true };
    return { sortKey: 'RELEVANCE', reverse: false };
  }

  function gqlOnce(queryStr, vars, rootField) {
    return fetch(API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query: queryStr, variables: vars }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json.errors) throw new Error(JSON.stringify(json.errors));
        return json.data[rootField];
      });
  }

  // One retry for transient network failures (e.g. throttled responses that
  // die before a body exists). A second failure still surfaces via showError.
  function gql(queryStr, vars, rootField) {
    return gqlOnce(queryStr, vars, rootField).catch(function (err) {
      var msg = String(err && err.message || err);
      if (msg.indexOf('Failed to fetch') === -1 && msg.indexOf('NetworkError') === -1) throw err;
      return new Promise(function (resolve) { setTimeout(resolve, 600); }).then(function () {
        return gqlOnce(queryStr, vars, rootField);
      });
    });
  }

  function query(vars) {
    return gql(QUERY, vars, 'search');
  }

  // Listing fetch for one availability stream/page, using the uncapped
  // products() engine when the current filters allow it.
  function listingQuery(available, after) {
    var pq = productsQueryString(available);
    if (pq === null) {
      return query(listingVars(available, after));
    }
    var s = productsSortKey(getSort());
    return gql(PRODUCTS_QUERY, {
      q: pq,
      sk: s.sortKey,
      rev: s.reverse,
      n: cfg.perPage,
      after: after || null,
    }, 'products');
  }

  /* ---------- Rendering ---------- */

  // All API-sourced text (titles, facet labels) is injected via innerHTML, so
  // it must be escaped: card names legitimately contain &, ", < (e.g.
  // 'Mew ex #232 "Bubble Mew"').
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(amount) {
    var n = Math.round(parseFloat(amount));
    return n.toLocaleString('tr-TR') + ' TL';
  }

  function cardHtml(p) {
    var available = p.availableForSale;
    var variant = (p.variants.nodes[0] || {});
    var variantId = variant.id ? variant.id.split('/').pop() : '';
    var url = p.onlineStoreUrl || (cfg.origin + '/products/' + p.handle);
    var img = p.featuredImage;
    var second = p.images.nodes[1];
    var ratio = img && img.width && img.height ? img.width / img.height : 1;
    var price = parseFloat(p.priceRange.minVariantPrice.amount);
    var compare = p.compareAtPriceRange && p.compareAtPriceRange.minVariantPrice
      ? parseFloat(p.compareAtPriceRange.minVariantPrice.amount) : 0;
    var onSale = compare > price;

    var imgHtml = img
      ? '<div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio:' + ratio + '">' +
        '<img src="' + img.url + '" alt="' + esc(img.altText || p.title) + '" loading="lazy" class="motion-reduce" width="' + (img.width || '') + '" height="' + (img.height || '') + '"></div>'
      : '<div class="product-card__image product-card__image--main" style="--aspect-ratio:1"></div>';
    var secondHtml = (cfg.showSecondImg && second)
      ? '<div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio:' + ratio + '">' +
        '<img src="' + second.url + '" alt="" loading="lazy" class="motion-reduce"></div>'
      : '';

    // Badges
    var badge = '';
    if (!available && cfg.showSoldoutBadge) {
      badge += '<span class="f-badge f-badge--soldout" aria-hidden="true">' + cfg.i18n.soldOut + '</span>';
    } else if (available && onSale && cfg.showSaleBadge) {
      var pct = Math.round(((compare - price) / compare) * 100);
      badge += '<span class="f-badge f-badge--sale" aria-hidden="true">-' + pct + '%</span>';
    }
    if (badge) badge = '<div class="product-card__badge">' + badge + '</div>';

    // Price (hidden entirely when sold out, matching theme)
    var priceHtml = '<div class="f-price f-price--' + cfg.alignment + (available ? '' : ' f-price--sold-out') + (onSale ? ' f-price--on-sale' : '') + '">';
    if (available) {
      if (onSale) {
        priceHtml += '<div class="f-price__sale"><span class="f-price-item f-price-item--sale">' + money(price) + '</span>' +
          '<span class="f-price-item f-price-item--regular"><s>' + money(compare) + '</s></span></div>';
      } else {
        priceHtml += '<div class="f-price__regular"><span class="f-price-item f-price-item--regular">' + money(price) + '</span></div>';
      }
    }
    priceHtml += '</div>';

    // Add-to-cart (single-variant); is="product-form" hooks the theme's AJAX cart
    var atc = '';
    if (variantId) {
      atc =
        '<form action="' + cfg.origin + '/cart/add" method="post" enctype="multipart/form-data"' +
        ' class="product-card__actions product-card__main-actions" novalidate="novalidate" is="product-form" data-type="add-to-cart-form">' +
        '<input type="hidden" name="id" value="' + variantId + '" class="product-variant-id"' + (available ? '' : ' disabled') + '>' +
        '<button type="submit" name="add" class="product-card__atc product-card__action-button btn ' + cfg.buttonStyle + '"' + (available ? '' : ' disabled') + '>' +
        '<div class="btn__text flex gap-1 items-center">' +
        '<span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">' + (cfg.cartIcon || '') + '</span>' +
        '<span class="product-card__atc-text product-card__action-text">' +
        (available ? cfg.i18n.addToCart : cfg.i18n.soldOut) +
        '</span></div></button></form>';
    }

    return (
      '<div class="f-column product-column">' +
      '<div class="product-card product-card-style-' + cfg.pcardStyle + (cfg.pcardStyle === 'card' ? ' color-' + cfg.pcardColorScheme : '') + (available ? '' : ' product-card--sold-out') + '">' +
      '<div class="product-card__wrapper h-full">' +
      '<div class="product-card__image-wrapper' + (second && cfg.showSecondImg ? '' : ' product-card__image-wrapper--main-only') + ' bg-none">' +
      '<a href="' + url + '" aria-label="' + esc(p.title) + '" tabindex="-1"><span class="block">' + imgHtml + secondHtml + '</span></a>' +
      badge + atc +
      '</div>' +
      '<div class="product-card__info text-' + cfg.alignment + '">' +
      '<h3 class="product-card__title text-pcard-title"><a class="reversed-link block" href="' + url + '"><span class="reversed-link__text">' + esc(p.title) + '</span></a></h3>' +
      priceHtml +
      '</div></div></div></div>'
    );
  }

  function renderFacets(filters) {
    var open = (cfg.expandGroups || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    var html = filters.map(function (f) {
      if (!f.values.length) return '';
      var isOpen = open.indexOf(f.label) !== -1;
      var body;
      if (f.type === 'PRICE_RANGE') {
        var p = new URLSearchParams(location.search);
        body =
          '<div class="sf-price-range flex items-center gap-2">' +
          '<input type="number" inputmode="numeric" class="form-control" data-sf-price-min placeholder="Min" value="' + (p.get('filter.v.price.gte') || '') + '">' +
          '<span>–</span>' +
          '<input type="number" inputmode="numeric" class="form-control" data-sf-price-max placeholder="Max" value="' + (p.get('filter.v.price.lte') || '') + '">' +
          '</div>';
      } else {
        body = '<ul class="sf-facet-list">' + f.values.map(function (v) {
          if (v.count === 0 && !isChecked(f.id, v.label)) return '';
          var id = 'sf-' + f.id + '-' + v.label;
          var exact = exactFacetCount(f.id, v.label);
          // Without an exact number, a raw 1000 is the search cap ("1000+").
          var countLabel = exact !== null
            ? exact.toLocaleString('tr-TR')
            : (v.count >= 1000 ? '1.000+' : String(v.count));
          return (
            '<li><label class="sf-facet-value flex items-center gap-2" for="' + id.replace(/[^a-z0-9]/gi, '') + '">' +
            '<input type="checkbox" id="' + id.replace(/[^a-z0-9]/gi, '') + '" data-sf-filter-id="' + f.id + '" data-sf-value="' + esc(v.label) + '"' + (isChecked(f.id, v.label) ? ' checked' : '') + '>' +
            '<span class="sf-facet-label">' + esc(v.label) + '</span>' +
            '<span class="sf-facet-count text-subtext">(' + countLabel + ')</span>' +
            '</label></li>'
          );
        }).join('') + '</ul>';
      }
      return (
        '<details class="sf-facet"' + (isOpen ? ' open' : '') + '>' +
        '<summary class="sf-facet__summary flex items-center justify-between"><span class="font-body-bolder">' + esc(f.label) + '</span></summary>' +
        '<div class="sf-facet__body">' + body + '</div>' +
        '</details>'
      );
    }).join('');
    els.facets.innerHTML = html;
    if (els.facetsMobile) els.facetsMobile.innerHTML = html;
    bindFacetEvents(root);
  }

  function renderActive() {
    var p = new URLSearchParams(location.search);
    var chips = [];
    p.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0) return;
      var label = value;
      if (key === 'filter.v.availability') label = value === '1' ? cfg.i18n.inStock : cfg.i18n.outStock;
      if (key === 'filter.v.price.gte') label = '≥ ' + value + ' TL';
      if (key === 'filter.v.price.lte') label = '≤ ' + value + ' TL';
      chips.push(
        '<button type="button" class="sf-chip btn btn--secondary" data-sf-remove-key="' + esc(key) + '" data-sf-remove-val="' + esc(value) + '">' +
        esc(label) + ' ✕</button>'
      );
    });
    if (chips.length) {
      chips.push('<button type="button" class="sf-chip sf-chip--clear" data-sf-clear>' + cfg.i18n.clearAll + '</button>');
      els.active.innerHTML = chips.join('');
      els.active.hidden = false;
    } else {
      els.active.innerHTML = '';
      els.active.hidden = true;
    }
  }

  function bindFacetEvents(scope) {
    scope.querySelectorAll('[data-sf-filter-id]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        toggleFilterParam(cb.getAttribute('data-sf-filter-id'), cb.getAttribute('data-sf-value'), cb.checked);
      });
    });
    var min = scope.querySelector('[data-sf-price-min]');
    var max = scope.querySelector('[data-sf-price-max]');
    function applyPrice() {
      setPrice(min && min.value, max && max.value);
    }
    if (min) min.addEventListener('change', applyPrice);
    if (max) max.addEventListener('change', applyPrice);
  }

  /* ---------- Fetch + paint ---------- */

  function listingVars(available, after) {
    var s = sortToKey(getSort());
    return {
      q: cfg.scope,
      f: listingFilters(available),
      sk: s.sortKey,
      rev: s.reverse,
      n: cfg.perPage,
      after: after || null,
    };
  }

  function setEmpty(show) {
    els.empty.style.setProperty('display', show ? 'flex' : 'none', 'important');
  }

  function refresh() {
    if (els.sort) els.sort.value = getSort();
    var mode = availabilityMode();
    state.gen += 1;
    var gen = state.gen;
    state.streams = mode.streams;
    state.streamIdx = 0;
    state.after = null;
    state.hasNext = false;
    state.loading = true;
    els.grid.setAttribute('aria-busy', 'true');

    var s = sortToKey(getSort());
    var facetVars = { q: cfg.scope, f: facetFilters(mode), sk: s.sortKey, rev: s.reverse, n: 1, after: null };

    Promise.all([query(facetVars), listingQuery(mode.streams[0], null), exactCountsReady])
      .then(function (res) {
        if (gen !== state.gen) return; // stale: a newer refresh superseded this one
        var facetData = res[0];
        var listData = res[1];
        renderFacets(facetData.productFilters);
        renderActive();
        // search caps totalCount at 1,000; the unfiltered view has a
        // precomputed exact total, and when the uncapped products() engine
        // is listing, an exact 1,000 really means "1,000 or more".
        var capped = facetData.totalCount >= 1000 && productsQueryString(true) !== null;
        var exactTotal = exactCounts && !hasActiveFilters() ? exactCounts.total : null;
        els.count.textContent = exactTotal !== null
          ? exactTotal.toLocaleString('tr-TR') + ' ' + cfg.i18n.products
          : capped
            ? '1.000+ ' + cfg.i18n.products
            : facetData.totalCount.toLocaleString('tr-TR') + ' ' + cfg.i18n.products;
        setEmpty(facetData.totalCount === 0);
        els.grid.innerHTML = listData.edges.map(function (e) { return cardHtml(e.node); }).join('');
        state.after = listData.pageInfo.endCursor;
        state.hasNext = listData.pageInfo.hasNextPage;
        updateLoadMore();
      })
      .catch(function (err) {
        if (gen !== state.gen) return;
        showError(err);
      })
      .finally(function () {
        if (gen !== state.gen) return;
        state.loading = false;
        els.grid.removeAttribute('aria-busy');
        maybeLoadNext();
      });
  }

  // IntersectionObserver only fires on in/out-of-view *transitions*. If the
  // sentinel is already in view when a load finishes (initial paint, short
  // result sets, tall viewports), no transition happens and pagination would
  // stall — so after every load, keep pulling while the sentinel is in range.
  function maybeLoadNext() {
    if (!els.sentinel) return;
    requestAnimationFrame(function () {
      var rect = els.sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 600) loadMore();
    });
  }

  function loadMore() {
    var moreInStream = state.hasNext;
    var moreStreams = state.streamIdx < state.streams.length - 1;
    if (state.loading || (!moreInStream && !moreStreams)) return;
    state.loading = true;
    var gen = state.gen;

    var available, after;
    if (moreInStream) {
      available = state.streams[state.streamIdx];
      after = state.after;
    } else {
      // Current stream exhausted -> advance to the next (sold-out) stream.
      state.streamIdx += 1;
      available = state.streams[state.streamIdx];
      after = null;
    }

    if (els.spinner) els.spinner.hidden = false;
    listingQuery(available, after)
      .then(function (data) {
        if (gen !== state.gen) return; // stale: sort/filters changed while in flight
        state.after = data.pageInfo.endCursor;
        state.hasNext = data.pageInfo.hasNextPage;
        els.grid.insertAdjacentHTML('beforeend', data.edges.map(function (e) { return cardHtml(e.node); }).join(''));
        updateLoadMore();
      })
      .catch(function (err) {
        if (gen !== state.gen) return;
        showError(err);
      })
      .finally(function () {
        if (gen !== state.gen) return;
        state.loading = false;
        if (els.spinner) els.spinner.hidden = true;
        maybeLoadNext();
      });
  }

  // With infinite scroll active, the button stays hidden (kept only as a
  // no-JS / no-IntersectionObserver fallback).
  var infiniteScroll = 'IntersectionObserver' in window;

  function updateLoadMore() {
    var moreStreams = state.streamIdx < state.streams.length - 1;
    var hasMore = state.hasNext || moreStreams;
    els.loadMore.hidden = infiniteScroll || !hasMore;
  }

  function showError(err) {
    // No silent fallback: surface the failure so it can be debugged.
    els.grid.innerHTML = '<div class="sf-error">' + cfg.i18n.error + '<pre style="white-space:pre-wrap">' + String(err && err.message || err) + '</pre></div>';
    console.error('[singles-filter]', err);
  }

  /* ---------- Wiring ---------- */

  if (els.sort) {
    els.sort.addEventListener('change', function () {
      var p = new URLSearchParams(location.search);
      p.set('sort_by', els.sort.value);
      pushState(p);
    });
  }
  els.loadMore.addEventListener('click', loadMore);
  root.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-sf-remove-key]');
    if (rm) {
      toggleFilterParam(rm.getAttribute('data-sf-remove-key'), rm.getAttribute('data-sf-remove-val'), false);
      return;
    }
    if (e.target.closest('[data-sf-clear]')) clearAll();
  });

  // Mobile filter drawer
  var drawer = root.querySelector('[data-sf-drawer]');
  var openBtn = root.querySelector('[data-sf-open-drawer]');
  var closeBtn = root.querySelector('[data-sf-close-drawer]');
  if (openBtn && drawer) {
    openBtn.addEventListener('click', function () {
      drawer.setAttribute('open', '');
    });
  }
  if (closeBtn && drawer) {
    closeBtn.addEventListener('click', function () {
      drawer.removeAttribute('open');
    });
  }

  if (els.sentinel && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '600px' }).observe(els.sentinel);
  }

  refresh();
})();
